import { useMemo } from "react"
import type { SummaryActionNotice } from "modules/permit/components/PermitRequestSummaryPanel"
import type { CancelReasonOption, PermitDetail, PermitWorkflowTimeline } from "modules/permit/api/permitService"
import {
  isCancelledStatus,
  isRequesterInformationStatus,
  isRevisionStatus,
  normalizePermitStatus,
} from "modules/permit/constants/permitStatus"
import {
  GUID_PATTERN,
  coerceStringValue,
  formatDateTimeDisplay,
  normalizeGuidValue,
  tryParseJsonValue,
} from "../utils/pageHelpers"

interface FormattedWorkflowActionNote {
  stageName?: string | null
  note?: string | null
  actedBy?: string | null
  actedAt?: string | null
}

interface UsePermitSummaryActionNoticeParams {
  permitDetail: PermitDetail | null
  permitStatusCode?: string | null
  cancelReasons: CancelReasonOption[]
  workflowTimeline: PermitWorkflowTimeline | null
  cancelActorName?: string | null
  normalizedRequesterId?: string | null
  normalizedControllerId?: string | null
  formattedLatestRevisionNote: FormattedWorkflowActionNote | null
  formattedLatestRequestReviewNote: FormattedWorkflowActionNote | null
}

export const usePermitSummaryActionNotice = ({
  permitDetail,
  permitStatusCode,
  cancelReasons,
  workflowTimeline,
  cancelActorName,
  normalizedRequesterId,
  normalizedControllerId,
  formattedLatestRevisionNote,
  formattedLatestRequestReviewNote,
}: UsePermitSummaryActionNoticeParams): SummaryActionNotice | null =>
  useMemo(() => {
    if (!permitDetail) return null

    const meta = permitDetail.metadata ?? {}
    const normalizedStatus = normalizePermitStatus(permitStatusCode)

    const resolveMetaText = (...keys: string[]) => {
      for (const key of keys) {
        if (Object.prototype.hasOwnProperty.call(meta, key)) {
          const text = coerceStringValue((meta as any)[key])
          if (text) return text
        }
      }
      return ""
    }

    const sanitizeName = (name: string) => {
      if (!name) return ""
      return GUID_PATTERN.test(name.trim()) ? "" : name
    }

    const resolveActorFromId = (userId?: string | null) => {
      const normalized = normalizeGuidValue(userId)
      if (!normalized) {
        return ""
      }

      const requesterIdNorm = normalizeGuidValue(permitDetail.requesterId)
      if (normalized === requesterIdNorm && permitDetail.requesterName) {
        return permitDetail.requesterName
      }

      const controllerIdNorm = normalizeGuidValue(
        (permitDetail.metadata as any)?.controllerId ?? (permitDetail.metadata as any)?.controller,
      )
      if (normalized === controllerIdNorm) {
        const controllerLabel =
          (permitDetail.metadata as any)?.controllerLabel ?? (permitDetail.metadata as any)?.controllerName
        if (typeof controllerLabel === "string" && controllerLabel.trim()) {
          return controllerLabel
        }
      }

      return ""
    }

    if (isCancelledStatus(normalizedStatus)) {
      const reasonCode = resolveMetaText("cancelReason")
      const reasonText = resolveMetaText("cancelReasonTxt", "lastCancelDetail")
      const normalizedReasonCode = reasonCode ? reasonCode.trim().toLowerCase() : ""
      const reasonFromMaster =
        normalizedReasonCode.length > 0
          ? cancelReasons.find((reason) => reason.code?.trim().toLowerCase() === normalizedReasonCode)?.description ??
            ""
          : ""

      const cancelHistory = (workflowTimeline?.history ?? [])
        .filter((history) => history.statusAfter?.toUpperCase() === "CANCELLED")
        .sort((a, b) => {
          const ta = Date.parse(a.actedAt)
          const tb = Date.parse(b.actedAt)
          if (Number.isNaN(ta) || Number.isNaN(tb)) return 0
          return tb - ta
        })

      const historyActorName =
        cancelHistory[0]?.actedByUserName && GUID_PATTERN.test(cancelHistory[0].actedByUserName.trim())
          ? ""
          : coerceStringValue(cancelHistory[0]?.actedByUserName)

      const actor =
        cancelActorName ||
        sanitizeName(resolveMetaText("cancelByUserName", "cancelByName", "cancelByDisplayName", "cancelBy")) ||
        sanitizeName(historyActorName) ||
        cancelHistory[0]?.actedByRoleName ||
        resolveActorFromId(cancelHistory[0]?.actedByUserId)

      const timestamp = formatDateTimeDisplay((meta as any).cancelDate ?? (meta as any).lastCancelDate)

      return {
        type: "cancel",
        title: "ใบงานถูกยกเลิก",
        message: (reasonText || reasonFromMaster || reasonCode || "").trim() || undefined,
        actor: actor || undefined,
        timestamp: timestamp || undefined,
      }
    }

    if (isRevisionStatus(normalizedStatus)) {
      const title = formattedLatestRevisionNote?.stageName
        ? `ใบงานถูกส่งกลับจาก ${formattedLatestRevisionNote.stageName}`
        : "ใบงานถูกส่งกลับเพื่อแก้ไข"

      return {
        type: "revision",
        title,
        message: formattedLatestRevisionNote?.note ?? undefined,
        actor: formattedLatestRevisionNote?.actedBy ?? undefined,
        timestamp: formattedLatestRevisionNote?.actedAt ?? undefined,
      }
    }

    if (isRequesterInformationStatus(normalizedStatus)) {
      const isExcavationPermit = (permitDetail.permitTypeCode ?? "").trim().toUpperCase() === "EXCAVATION_WORK"
      const defaultTitle = isExcavationPermit
        ? "รอให้ผู้ขอดำเนินการกรอกข้อมูลประกอบการขออนุญาตกลบดิน"
        : "รอผู้ขอให้ข้อมูลเพิ่มเติม"
      const title = formattedLatestRequestReviewNote?.stageName
        ? `รอผู้ขอให้ข้อมูลเพิ่มเติมจาก ${formattedLatestRequestReviewNote.stageName}`
        : defaultTitle

      return {
        type: "request-info",
        title,
        message: formattedLatestRequestReviewNote?.note ?? undefined,
        actor: formattedLatestRequestReviewNote?.actedBy ?? undefined,
        timestamp: formattedLatestRequestReviewNote?.actedAt ?? undefined,
      }
    }

    const latestExtendHistory = workflowTimeline?.extensionHistory?.[0] ?? null
    const latestExtendMeta =
      tryParseJsonValue<Record<string, unknown>>((meta as any).lastExtendPermit) ?? null
    const extendReason =
      coerceStringValue(latestExtendHistory?.reason) ||
      coerceStringValue(latestExtendMeta?.reason) ||
      resolveMetaText("extReason")
    const extendRequestedAt =
      formatDateTimeDisplay(latestExtendHistory?.newExtendedEnd) ||
      formatDateTimeDisplay((meta as any).extDate) ||
      formatDateTimeDisplay(latestExtendMeta?.newExtendedEnd) ||
      formatDateTimeDisplay(latestExtendHistory?.requestedAt) ||
      formatDateTimeDisplay(latestExtendMeta?.requestedAt)
    const extendRequestedById =
      normalizeGuidValue(latestExtendHistory?.requestedByUserId) ||
      normalizeGuidValue(latestExtendMeta?.requestedBy) ||
      normalizeGuidValue((meta as any).extBy)
    const extendActor =
      sanitizeName(coerceStringValue(latestExtendHistory?.requestedByUserName)) ||
      sanitizeName(coerceStringValue(latestExtendMeta?.requestedByUserName)) ||
      resolveActorFromId(extendRequestedById)

    if (extendReason || extendRequestedAt || extendActor) {
      return {
        type: "extend-permit",
        title: "ใบงานถูกขอต่อเวลาทำงาน",
        message: extendReason || undefined,
        actor: extendActor || undefined,
        timestamp: extendRequestedAt || undefined,
      }
    }

    const latestChangeWork =
      tryParseJsonValue<Record<string, unknown>>((meta as any).lastChangeWorkTime) ?? null
    const changeReason =
      coerceStringValue(latestChangeWork?.reason) || resolveMetaText("chgDateReason", "changeWorkReason")
    const changeRequestedAt =
      formatDateTimeDisplay(latestChangeWork?.requestedAt) ||
      formatDateTimeDisplay((meta as any).chgDateAt ?? (meta as any).lastChangeWorkTimeAt)
    const changeRequestedById =
      normalizeGuidValue(latestChangeWork?.requestedBy) || normalizeGuidValue((meta as any).chgDateBy)
    const changeActor =
      changeRequestedById && changeRequestedById === normalizedRequesterId
        ? permitDetail.requesterName || undefined
        : changeRequestedById && changeRequestedById === normalizedControllerId
          ? coerceStringValue((meta as any).controllerLabel || (meta as any).controllerName) || undefined
          : undefined

    if (changeReason) {
      return {
        type: "change-work-time",
        title: "มีการเปลี่ยนแปลงเวลาการทำงาน",
        message: changeReason,
        actor: changeActor,
        timestamp: changeRequestedAt || undefined,
      }
    }

    return null
  }, [
    cancelActorName,
    cancelReasons,
    formattedLatestRequestReviewNote,
    formattedLatestRevisionNote,
    normalizedControllerId,
    normalizedRequesterId,
    permitDetail,
    permitStatusCode,
    workflowTimeline,
  ])
